import classNames from 'classnames';
import type { StaticImageData } from 'next/image';
import Image from 'next/image';
import Link from 'next/link';
import type { FC, ReactNode } from 'react';

import Icon from '@/components/Icon';
import { AnimatedButton } from '@/components/Landing/AnimatedButton';
import ContactFormSheet from '@/components/Landing/ContactFormSheet';
import Stack from '@/components/Landing/Stack';
import Text from '@/components/Text';

export type CaseStudyMeta = {
  label: string;
  value: string;
};

export type CaseStudyImage = {
  src: string;
  alt: string;
  width: number;
  height: number;
};

export type CaseStudySection = {
  eyebrow?: string;
  heading?: string;
  body: ReactNode;
  image?: CaseStudyImage;
  imagePosition?: 'left' | 'right';
};

type Props = {
  companyName: string;
  logo?: StaticImageData;
  tagline: string;
  meta: CaseStudyMeta[];
  heroImage: CaseStudyImage;
  intro: ReactNode;
  sections: CaseStudySection[];
  closingHeading: string;
  closing: ReactNode;
};

const CaseStudyArticle: FC<Props> = ({
  companyName,
  logo,
  tagline,
  meta,
  heroImage,
  intro,
  sections,
  closingHeading,
  closing,
}) => {
  return (
    <Stack
      className="pb-10"
      innerClassName="max-w-screen-lg gap-12 md:gap-16 lg:gap-24"
    >
      {/* Header */}
      <div className="flex w-full flex-col gap-6 md:gap-8">
        <Link
          href="/case-studies"
          className="flex w-fit items-center gap-2 text-jb-neutral-400 transition-colors hover:text-jb-neutral-100"
        >
          <Icon icon="material-symbols:arrow-back" />
          <Text variant="body-small">Back to Case Studies</Text>
        </Link>

        <div className="flex items-center justify-between gap-6">
          <Text
            variant="body-small"
            className="uppercase tracking-widest text-jb-neutral-600"
          >
            Case Study
          </Text>
          {logo && (
            <Image
              src={logo}
              alt={`${companyName} logo`}
              className="h-auto w-28 shrink-0 object-contain sm:w-32 md:w-36"
            />
          )}
        </div>

        <div className="flex flex-col gap-4">
          <Text variant="display-2" as="h1" className="text-jb-neutral-100">
            {companyName}
          </Text>
          <Text variant="jb-h5" className="max-w-2xl text-jb-neutral-400">
            {tagline}
          </Text>
        </div>

        <div className="flex w-full flex-row flex-wrap gap-x-10 gap-y-4 border-y border-jb-neutral-800 py-6 md:gap-y-6 md:py-8">
          {meta.map(({ label, value }) => (
            <div key={label} className="flex flex-col gap-1">
              <Text variant="body-small" className="text-jb-neutral-600">
                {label}
              </Text>
              <Text className="text-jb-neutral-400">{value}</Text>
            </div>
          ))}
        </div>
      </div>

      {/* Hero image */}
      <Image
        src={heroImage.src}
        alt={heroImage.alt}
        width={heroImage.width}
        height={heroImage.height}
        className="h-auto w-full rounded-xl md:rounded-2xl"
        priority
      />

      {/* Intro */}
      <Text variant="jb-h5" className="max-w-3xl text-jb-neutral-100">
        {intro}
      </Text>

      {/* Sections */}
      {sections.map(({ eyebrow, heading, body, image, imagePosition }) => (
        <div
          key={heading ?? eyebrow}
          className="flex w-full flex-col gap-8 lg:flex-row lg:gap-12"
        >
          {image && imagePosition !== 'right' && (
            <div className="flex-1">
              <Image
                src={image.src}
                alt={image.alt}
                width={image.width}
                height={image.height}
                className="h-auto w-full rounded-xl"
              />
            </div>
          )}
          <div
            className={classNames('flex flex-col gap-6', {
              'flex-1': !!image,
              'max-w-3xl': !image,
            })}
          >
            {(eyebrow || heading) && (
              <div className="flex flex-col gap-2">
                {eyebrow && (
                  <Text
                    variant="body-small"
                    className="uppercase tracking-widest text-jb-neutral-600"
                  >
                    {eyebrow}
                  </Text>
                )}
                {heading && (
                  <Text variant="h3" className="text-jb-neutral-100">
                    {heading}
                  </Text>
                )}
              </div>
            )}
            <div className="flex flex-col gap-4 text-jb-neutral-400">
              {body}
            </div>
          </div>
          {image && imagePosition === 'right' && (
            <div className="flex-1">
              <Image
                src={image.src}
                alt={image.alt}
                width={image.width}
                height={image.height}
                className="h-auto w-full rounded-xl"
              />
            </div>
          )}
        </div>
      ))}

      {/* CTA */}
      <div className="flex min-h-[60vh] w-full flex-col items-center justify-center gap-6 py-16 text-center md:min-h-[80vh] md:gap-8 md:py-24">
        <Text
          variant="display-2"
          as="h2"
          className="max-w-2xl text-center text-jb-neutral-100"
        >
          {closingHeading}
        </Text>
        <div className="flex max-w-lg flex-col items-center gap-4 text-center text-jb-neutral-400">
          {closing}
        </div>
        <ContactFormSheet
          sheetTriggerProps={{ asChild: true, className: 'mt-2' }}
        >
          <AnimatedButton
            label="Get started"
            icon="material-symbols:arrow-back"
          />
        </ContactFormSheet>
      </div>
    </Stack>
  );
};

export default CaseStudyArticle;
